import React, { useState } from "react";
import { Card, Input, Button, Tag, Form, message, Modal } from "antd";
import {
    User,
    Wallet,
    Lock,
    Mail,
    SlidersHorizontal,
    KeyRound,
    ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import reapi from "../apis/reapi";

export default function Settings() {
    const navigate = useNavigate();

    const userInfo = JSON.parse(localStorage.getItem("userInfo")) || {};

    return (
        <div className="w-full min-h-screen bg-[#f8f9ff] p-4 md:p-6">
            <div className="max-w-[1400px] mx-auto">
                <div className="mb-7">
                    <h1 className="text-3xl font-extrabold m-0">Settings</h1>
                    <p className="text-slate-600 mt-2">
                        Manage your account, preferences and application settings.
                    </p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <ProfileSettings userInfo={userInfo} />
                    <AccountSummary userInfo={userInfo} navigate={navigate} />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
                    <PasswordCard />
                    <ComingSoonCard
                        icon={<ShieldCheck />}
                        title="Security & Privacy"
                        desc="Two-factor authentication, sessions and account security."
                    />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
                    <ComingSoonCard
                        icon={<Mail />}
                        title="Email Notifications"
                        desc="Manage emails about your account and activity."
                    />

                    <ComingSoonCard
                        icon={<SlidersHorizontal />}
                        title="Optimization Settings"
                        desc="Set default optimization preferences."
                    />

                    <ComingSoonCard
                        icon={<KeyRound />}
                        title="API Access"
                        desc="Access your API key to integrate with ShipLess AI."
                    />
                </div>
            </div>
        </div>
    );
}

function ProfileSettings({ userInfo }) {
    return (
        <Card className="rounded-2xl shadow-sm">
            <div className="flex items-center gap-4 mb-6">
                <IconBox icon={<User />} bg="bg-purple-50" color="text-purple-600" />
                <h2 className="text-xl font-bold m-0">Profile Settings</h2>
            </div>

            <div className="space-y-5">
                <Field label="Name" value={userInfo?.name || ""} />
                <Field label="Email Address" value={userInfo?.email || ""} />
                <Field label="Phone Number" value={userInfo?.mobile || userInfo?.phone || ""} />
                {/* <Field label="Business / Store Name (Optional)" value={userInfo?.storeName || ""} /> */}

                {/* <div className="flex justify-end">
                    <Button type="primary" className="bg-purple-600 px-8">
                        Save Changes
                    </Button>
                </div> */}
            </div>
        </Card>
    );
}

function AccountSummary({ userInfo, navigate }) {
    return (
        <Card className="rounded-2xl shadow-sm">
            <div className="flex items-center gap-4 mb-6">
                <IconBox icon={<Wallet />} bg="bg-purple-50" color="text-purple-600" />
                <h2 className="text-xl font-bold m-0">Account Summary</h2>
            </div>

            <div className="border rounded-xl overflow-hidden">
                <SummaryRow label="Member Since" value={userInfo?.createdAt
                ? new Date(
                  userInfo?.createdAt
                ).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
                : "-"} />
                <SummaryRow label="Current Plan" value="Free" />
                <SummaryRow
                    label="Available Credits"
                    value={`${userInfo?.wallet || 0} Credits`}
                    highlight
                />
                {/* <SummaryRow label="Total Credits Added" value="—" /> */}
                {/* <SummaryRow label="Total Credits Used" value="—" /> */}
            </div>

            <Button
                block
                size="large"
                className="mt-8 border-purple-500 text-purple-600 font-semibold"
                onClick={() => navigate("/transactions")}
            >
                View Transactions
            </Button>
        </Card>
    );
}

function PasswordCard() {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();

    const token = localStorage.getItem("token");


    const handleChangePassword = async (values) => {


        try {
            await reapi.post(
                "/self-password-change",
                {
                    oldPassword: values.oldPassword,
                    newPassword: values.newPassword,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            message.success("Password changed successfully");

            form.resetFields();
            setOpen(false);

        } catch (error) {
            console.log(error)
            toast.error(error?.response?.data?.message)
            // setOpen(false);

        }



    };

    return (
        <>
            <Card className="rounded-2xl shadow-sm">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <IconBox icon={<Lock />} bg="bg-purple-50" color="text-purple-600" />

                        <div>
                            <h3 className="font-bold m-0">Password</h3>
                            <p className="text-slate-500 m-0 mt-1">
                                Update your password to keep your account secure.
                            </p>
                        </div>
                    </div>

                    <Button
                        className="border-purple-500 text-purple-600 font-semibold"
                        onClick={() => setOpen(true)}
                    >
                        Change Password
                    </Button>
                </div>
            </Card>

            <Modal
                title="Change Password"
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                centered
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleChangePassword}
                    className="mt-5"
                >
                    <Form.Item
                        label="Current Password"
                        name="oldPassword"
                        rules={[{ required: true, message: "Enter current password" }]}
                    >
                        <Input.Password size="large" placeholder="Current password" />
                    </Form.Item>

                    <Form.Item
                        label="New Password"
                        name="newPassword"
                        rules={[
                            { required: true, message: "Enter new password" },
                            { min: 6, message: "Password must be at least 6 characters" },
                        ]}
                    >
                        <Input.Password size="large" placeholder="New password" />
                    </Form.Item>

                    <Form.Item
                        label="Confirm Password"
                        name="confirmPassword"
                        dependencies={["newPassword"]}
                        rules={[
                            { required: true, message: "Confirm password" },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue("newPassword") === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error("Passwords do not match"));
                                },
                            }),
                        ]}
                    >
                        <Input.Password size="large" placeholder="Confirm password" />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                        className="bg-purple-600 font-bold"
                    >
                        Update Password
                    </Button>
                </Form>
            </Modal>
        </>
    );
}

function ComingSoonCard({ icon, title, desc }) {
    return (
        <Card className="rounded-2xl shadow-sm">
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <IconBox icon={icon} bg="bg-slate-100" color="text-purple-600" />

                    <div>
                        <h3 className="font-bold m-0">{title}</h3>
                        <p className="text-slate-500 m-0 mt-1">{desc}</p>
                    </div>
                </div>

                <Tag color="purple">Coming Soon</Tag>
            </div>
        </Card>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <label className="font-semibold block mb-2">{label}</label>
            <Input size="large" value={value} readOnly className="rounded-lg" />
        </div>
    );
}

function SummaryRow({ label, value, highlight }) {
    return (
        <div className="flex justify-between items-center px-5 py-4 border-b last:border-b-0">
            <span className="text-slate-600">{label}</span>
            <b className={highlight ? "text-purple-600" : ""}>{value}</b>
        </div>
    );
}

function IconBox({ icon, bg, color }) {
    return (
        <div
            className={`w-12 h-12 rounded-xl ${bg} ${color} flex items-center justify-center shrink-0`}
        >
            {icon}
        </div>
    );
}